import { useState, useEffect, useCallback, useRef } from 'react';
import { useNotification } from '../contexts/NotificationContext';
import { formatError, isRecoverableError } from '../utils/errorHandler';

/**
 * Hook para fazer queries (GET) com cache e retry automático
 * 
 * @param {Function} queryFn - Função que retorna uma Promise
 * @param {Array} dependencies - Array de dependências para refetch
 * @param {Object} options - Opções de configuração
 * @param {boolean} options.enabled - Se a query deve ser executada (padrão: true)
 * @param {number} options.retry - Número de tentativas em caso de erro (padrão: 0)
 * @param {number} options.retryDelay - Delay entre tentativas em ms (padrão: 1000)
 * @param {boolean} options.showError - Se deve mostrar notificação de erro (padrão: true)
 * @param {boolean} options.showLoading - Se deve gerenciar estado de loading (padrão: true)
 * 
 * @returns {Object} { data, error, loading, refetch, isRefetching }
 */
export const useQuery = (queryFn, dependencies = [], options = {}) => {
  const {
    enabled = true,
    retry = 0,
    retryDelay = 1000,
    showError = true,
    showLoading = true,
  } = options;

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(showLoading && enabled);
  const [isRefetching, setIsRefetching] = useState(false);
  
  const { showError: showErrorNotification } = useNotification();
  const retryCountRef = useRef(0);
  const abortControllerRef = useRef(null);

  const executeQuery = useCallback(async (isRefetch = false) => {
    // Cancelar requisição anterior se existir
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Criar novo AbortController
    abortControllerRef.current = new AbortController();

    try {
      if (isRefetch) {
        setIsRefetching(true);
      } else if (showLoading) {
        setLoading(true);
      }

      setError(null);
      retryCountRef.current = 0;

      const result = await queryFn(abortControllerRef.current.signal);
      
      setData(result);
      setError(null);
      retryCountRef.current = 0;

      return result;
    } catch (err) {
      // Ignorar erros de abort
      if (err.name === 'AbortError') {
        return;
      }

      const formattedError = formatError(err);

      // Tentar novamente se for erro recuperável e ainda houver tentativas
      if (isRecoverableError(err) && retryCountRef.current < retry) {
        retryCountRef.current++;
        
        await new Promise((resolve) => setTimeout(resolve, retryDelay * retryCountRef.current));
        
        return executeQuery(isRefetch);
      }

      setError(formattedError);
      setData(null);

      if (showError) {
        showErrorNotification(formattedError.message, 'Erro ao carregar dados');
      }

      // Não relançamos o erro: ele é exposto via estado (`error`),
      // evitando quebrar a árvore de renderização e facilitando testes.
      return null;
    } finally {
      if (isRefetch) {
        setIsRefetching(false);
      } else if (showLoading) {
        setLoading(false);
      }
    }
  }, [queryFn, retry, retryDelay, showError, showLoading, showErrorNotification]);

  // Executar query quando dependências mudarem
  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    executeQuery(false);

    // Cleanup: cancelar requisição se componente desmontar
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ...dependencies]);

  const refetch = useCallback(() => {
    return executeQuery(true);
  }, [executeQuery]);

  return {
    data,
    error,
    loading,
    isRefetching,
    refetch,
  };
};

